import React, { Fragment, useRef, useState } from 'react'
import Card from '../../../../../components/atoms/Card'
import Container from '../../../../../components/atoms/Container'
import Heading from '../../../../../components/atoms/Heading'
import Button from '../../../../../components/molecules/Button'
import { useTranslation } from '../../../../../providers/I18n'
import { BankCreditDetail } from '../../../../../types/credits'
import { StyledDescription } from '../../../../../components/styled/Description'
import { useQuery } from 'react-query'
import { useParams } from 'react-router-dom'
import {
  addCreditDocument,
  addCreditHistory,
  getCredit,
  updateCredit,
} from '../../../../../services/credits'
import { getCreditColor } from '../../../../../utils/credits'
import { useUser } from '../../../../../providers/User'
import CreditWaitAction from '../../../shared/CreditWaitAction'
import Textarea from '../../../../../components/atoms/Textarea'
import { CreditPhases, CreditSteps, Role } from '../../../../../types/enums'
import Spinner from '../../../../../components/atoms/Spinner'
import FileDropper, {
  FileWithBuffer,
} from '../../../../../components/molecules/FileDropper'
import { uploadDocuments } from '../../../../../utils/documents'
import DateInput from '../../../../../components/atoms/DateInput'
import { addCommissionCredit } from '../../../../../services/commissions'
import { useAlert } from '../../../../../providers/Alert'

const Phase3Fondeo: React.FC = () => {
  const commentRef = useRef<HTMLTextAreaElement>(null)
  const fundingDateRef = useRef<HTMLInputElement>(null)
  const documentsDateRef = useRef<HTMLInputElement>(null)

  const { code } = useParams()
  const { user } = useUser()
  const { setAlert } = useAlert()
  const [loading, setLoading] = useState<boolean>(false)
  const { getTranslation } = useTranslation()
  const [pantallaFondeoFile, setPantallaFondeoFile] = useState<
    FileWithBuffer[]
  >([])
  const {
    data: credit,
    isLoading,
    refetch,
  } = useQuery<BankCreditDetail>(['getCredit', code], () => getCredit(code!), {
    refetchOnWindowFocus: false,
  })

  let header
  let footer
  let content
  if (!credit && isLoading) {
    content = (
      <Container>
        <StyledDescription>Cargando credito...</StyledDescription>
      </Container>
    )
  } else if (credit) {
    const isActiveOwner = user!.code === credit.owners.phase3Operator?.code
    const color = getCreditColor(credit.phase)
    const creditPhaseStep = `${getTranslation!(
      `phases.${credit.phase}.title`
    )} - ${getTranslation!(`phases.${credit.phase}.steps.${credit.step}`)}`

    header = (
      <Container padding>
        <Heading type="3" color={color}>
          {creditPhaseStep}
        </Heading>
      </Container>
    )

    if (isActiveOwner || user?.role !== Role.Advisor) {
      const pantallaFondeoBuffer = pantallaFondeoFile
        ? pantallaFondeoFile.map((fileWithBuffer) => {
            fileWithBuffer.fixedName = 'pantallaFondeo'
            return fileWithBuffer.file
          })
        : []

      const handleBackToCloseValues = async () => {
        setLoading(true)
        const hasComment = !!commentRef.current?.value
        if (!hasComment) {
          setAlert!({
            severity: 'error',
            title: 'Error',
            description: 'Hacen falta campos requeridos',
          })
          setLoading(false)
        } else {
          await updateCredit(credit!.code, {
            phase: CreditPhases.Phase3,
            step: CreditSteps.Phase3Escriturado,
          }).then(async () => {
            const comment = `Regresando a Etapa 3 - Escrituración :: ${commentRef.current?.value}`
            const creditHistoryComment = {
              comment: comment,
              type: 'status',
              start: new Date(),
              change: {
                from: {
                  phase: credit.phase,
                  step: credit.step,
                },
                to: {
                  phase: CreditPhases.Phase3,
                  step: CreditSteps.Phase3Escriturado,
                },
              },
            }
            await addCreditHistory(credit.code, creditHistoryComment).then(
              () => {
                if (commentRef.current) {
                  commentRef.current.value = ''
                }
                refetch().then(() => {
                  setLoading(false)
                })
              }
            )
          })
        }
      }

      const handleSendToSignatureDate = async () => {
        setLoading(true)

        const hasComment = !!commentRef.current?.value
        const hasFondeo = !!fundingDateRef.current?.value
        const hasDocumentsDate = !!documentsDateRef.current?.value
        const hasDocuments = pantallaFondeoFile.length > 0
        if (!hasComment || !hasFondeo || !hasDocumentsDate || !hasDocuments) {
          setAlert!({
            severity: 'error',
            title: 'Error',
            description: 'Hacen falta campos requeridos',
          })
          setLoading(false)
        } else {
          await updateCredit(credit!.code, {
            phase: CreditPhases.Phase3,
            step: CreditSteps.Phase3Completed,
            isCompleted: true,
            meta: {
              fundingDate: fundingDateRef.current?.value,
              documentsDate: documentsDateRef.current?.value,
            },
          }).then(async () => {
            const documents: FileWithBuffer[] = []
            if (pantallaFondeoBuffer.length > 0) {
              documents.push(...pantallaFondeoFile)
            }
            const pushedCreditDocuments = await uploadDocuments(
              documents,
              `credit/${credit!.code.toString()}`
            )

            pushedCreditDocuments.forEach((document) => {
              addCreditDocument(credit.code, document!)
            })

            const comment = `Tramite Completado :: ${commentRef.current?.value}`

            const fundingDate = new Date(fundingDateRef.current?.value || '')
            fundingDate.setDate(fundingDate.getDate() + 1)

            const documentsDate = new Date(
              documentsDateRef.current?.value || ''
            )
            documentsDate.setDate(documentsDate.getDate() + 1)

            const creditHistoryComment = {
              comment: comment,
              documents: pushedCreditDocuments,
              meta: {
                fundingDate: new Date(fundingDate).toLocaleDateString('es-MX', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                }),
                documentsDate: new Date(documentsDate).toLocaleDateString(
                  'es-MX',
                  {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  }
                ),
              },
              type: 'status',
              start: new Date(),
              change: {
                from: {
                  owner: credit.owners.phase3Operator._id,
                  phase: credit.phase,
                  step: credit.step,
                },
                to: {
                  owner: undefined,
                  phase: CreditPhases.Phase3,
                  step: CreditSteps.Phase3Completed,
                },
              },
            }
            await addCreditHistory(credit.code, creditHistoryComment).then(
              async () => {
                if (commentRef.current) {
                  commentRef.current.value = ''
                }
                await addCommissionCredit(credit.code.toString())
                refetch().then(() => {
                  setLoading(false)
                })
              }
            )
          })
        }
      }

      content = (
        <Fragment>
          <StyledDescription>
            El tramite ya esta escriturado, es necesario capturar datos sobre
            fondeo.
          </StyledDescription>
          <DateInput label="Fecha de Fondeo" ref={fundingDateRef} required />
          <DateInput
            label="Fecha de Entrega Documentos"
            ref={documentsDateRef}
            required
          />
          <FileDropper
            label="Documentos Cobranza Banco"
            onChange={setPantallaFondeoFile}
            files={pantallaFondeoBuffer}
          />
          <Textarea label="Comentarios" ref={commentRef} required />
        </Fragment>
      )

      footer = (
        <Container align="right" direction="row" padding>
          {loading ? (
            <Spinner type="bar" />
          ) : (
            <>
              <Button
                onClick={handleBackToCloseValues}
                priority="secondary"
                color={color}
              >
                Regresar a Escrituración
              </Button>
              <Button onClick={handleSendToSignatureDate} color={color}>
                Tramite Fondeado!
              </Button>
            </>
          )}
        </Container>
      )
    } else {
      content = <CreditWaitAction owner={credit.owners.phase3Operator} />
    }
  }

  return (
    <div>
      <Card header={header} footer={footer}>
        {content}
      </Card>
    </div>
  )
}

export default Phase3Fondeo
